import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import type { Creator, CreatorSearchResult, CampaignDetail } from "@/lib/hiring-api";
import {
  useFindCreators,
  useSendOffer,
  useSendBulkOffers,
  useGetCampaign,
  useUpdateCampaign,
  useSetCreatorPreference,
  getFindCreatorsQueryKey,
  FindCreatorsSortBy,
} from "@/lib/hiring-api";
import { useQueryClient } from "@tanstack/react-query";
import { logCreate, logUpdate } from "@/lib/audit-log";
import {
  ChevronLeft,
  Search,
  Loader2,
  CheckCircle2,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  SlidersHorizontal,
  ExternalLink,
  Eye,
  EyeOff,
  Save,
  Heart,
  Star,
  Send,
  Copy,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  WIDE_TABLE_SCROLL,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";

function fmtGmv(val: number | null | undefined) {
  if (val == null) return "—";
  return "$" + Math.round(val).toLocaleString();
}

function fmtRoi(val: number | null | undefined) {
  if (val == null) return "—";
  return val.toFixed(1) + "x";
}

function fmtRate(video_prices: unknown) {
  if (!Array.isArray(video_prices) || video_prices.length === 0) return "—";
  const tiers = video_prices as { count: number; price: number }[];
  const tier = tiers.find((t) => t.count === 10) || tiers[0];
  if (!tier || !tier.price || !tier.count) return "—";
  const rate = (tier.price / tier.count).toFixed(0);
  return "$" + rate + "/vid";
}

interface EmailDraft {
  subject: string;
  preheader: string;
  greeting: string;
  headline: string;
  ctaText: string;
  cashPrizesDesc: string;
  commissionDesc: string;
}

export default function FindCreators() {
  const params = useParams();
  const id = params.id;
  const { toast } = useToast();

  function errToast(title: string, msgOrError: string | unknown) {
    const isErrObj = msgOrError && typeof msgOrError === "object";
    const apiErr = msgOrError as any;
    const msg = isErrObj
      ? (apiErr?.data?.error ?? apiErr?.message ?? "An error occurred")
      : (msgOrError as string);

    const copyText = isErrObj
      ? JSON.stringify({
          status: apiErr?.status,
          statusText: apiErr?.statusText,
          error: apiErr?.data?.error,
          code: apiErr?.data?.code,
          message: apiErr?.message,
          url: apiErr?.url,
        }, null, 2)
      : msg;

    toast({
      title,
      description: msg,
      variant: "destructive",
      action: (
        <ToastAction
          altText="Copy error"
          onClick={() => navigator.clipboard.writeText(copyText).catch(() => {})}
          className="shrink-0"
        >
          <Copy className="h-3.5 w-3.5 mr-1" />
          Copy
        </ToastAction>
      ),
    });
  }
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<string>(FindCreatorsSortBy.gmv_desc);
  const [page, setPage] = useState(1);

  const [minGmv, setMinGmv] = useState<number | undefined>(undefined);
  const [maxGmv, setMaxGmv] = useState<number | undefined>(undefined);
  const [minGmvInput, setMinGmvInput] = useState("");
  const [maxGmvInput, setMaxGmvInput] = useState("");

  const [minGmvPerVideo, setMinGmvPerVideo] = useState<number | undefined>(undefined);
  const [maxGmvPerVideo, setMaxGmvPerVideo] = useState<number | undefined>(undefined);
  const [minGmvPerVideoInput, setMinGmvPerVideoInput] = useState("");
  const [maxGmvPerVideoInput, setMaxGmvPerVideoInput] = useState("");

  const [minRoi, setMinRoi] = useState<number | undefined>(undefined);
  const [maxRoi, setMaxRoi] = useState<number | undefined>(undefined);
  const [minRoiInput, setMinRoiInput] = useState("");
  const [maxRoiInput, setMaxRoiInput] = useState("");

  const [minRate, setMinRate] = useState<number | undefined>(undefined);
  const [maxRate, setMaxRate] = useState<number | undefined>(undefined);
  const [minRateInput, setMinRateInput] = useState("");
  const [maxRateInput, setMaxRateInput] = useState("");

  const [gender, setGender] = useState<string | undefined>(undefined);

  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  const [sendingIds, setSendingIds] = useState<Set<string>>(new Set());
  const [savingTemplate, setSavingTemplate] = useState(false);

  const [previewCreator, setPreviewCreator] = useState<Creator | null>(null);
  const [draft, setDraft] = useState<EmailDraft | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkDraft, setBulkDraft] = useState<EmailDraft | null>(null);
  const [bulkSending, setBulkSending] = useState(false);

  const queryKey = getFindCreatorsQueryKey(id, {
    search: search || undefined,
    category: category || undefined,
    gender: gender || undefined,
    sortBy: sortBy as typeof FindCreatorsSortBy[keyof typeof FindCreatorsSortBy],
    page,
    minGmv,
    maxGmv,
    minGmvPerVideo,
    maxGmvPerVideo,
    minRoi,
    maxRoi,
    minRate,
    maxRate,
    showFavoritesOnly: showFavoritesOnly || undefined,
    showHidden: showHidden || undefined,
    activeOnly: false,
  });

  const { data, isLoading } = useFindCreators(id, {
    search: search || undefined,
    category: category || undefined,
    gender: gender || undefined,
    sortBy: sortBy as typeof FindCreatorsSortBy[keyof typeof FindCreatorsSortBy],
    page,
    minGmv,
    maxGmv,
    minGmvPerVideo,
    maxGmvPerVideo,
    minRoi,
    maxRoi,
    minRate,
    maxRate,
    showFavoritesOnly: showFavoritesOnly || undefined,
    showHidden: showHidden || undefined,
    activeOnly: false,
  });

  const { data: campaignData } = useGetCampaign(id);
  const campaign = campaignData as unknown as CampaignDetail | undefined;

  const updateCampaign = useUpdateCampaign({
    mutation: {
      onSuccess: (_, variables) => {
        logUpdate('hiring_campaigns', id, {}, variables.data as Record<string, any>, { action: 'save_email_template', campaign_id: id });
        toast({ title: "Template saved as default" });
        setSavingTemplate(false);
        queryClient.invalidateQueries({ queryKey: [`/api/v1/hiring/campaigns/${id}`] });
      },
      onError: () => {
        toast({ title: "Failed to save template", variant: "destructive" });
        setSavingTemplate(false);
      },
    },
  });

  const setPreference = useSetCreatorPreference({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getFindCreatorsQueryKey(id) });
      },
      onError: () => {
        toast({ title: "Failed to update preference", variant: "destructive" });
        queryClient.invalidateQueries({ queryKey: getFindCreatorsQueryKey(id) });
      },
    },
  });

  function toggleFavorite(creator: Creator) {
    const newVal = !creator.is_favorite;
    // Optimistic update
    queryClient.setQueriesData<CreatorSearchResult>(
      { queryKey: getFindCreatorsQueryKey(id) },
      (old) => {
        if (!old) return old;
        return {
          ...old,
          creators: old.creators.map((c) =>
            c.id === creator.id ? { ...c, is_favorite: newVal } : c
          ),
        };
      }
    );
    setPreference.mutate({
      id,
      creatorId: creator.id,
      data: { is_favorite: newVal, is_hidden: creator.is_hidden ?? false },
    });
  }

  function toggleHidden(creator: Creator) {
    const newVal = !creator.is_hidden;
    // Optimistic update
    queryClient.setQueriesData<CreatorSearchResult>(
      { queryKey: getFindCreatorsQueryKey(id) },
      (old) => {
        if (!old) return old;
        return {
          ...old,
          creators: old.creators.map((c) =>
            c.id === creator.id ? { ...c, is_hidden: newVal } : c
          ),
        };
      }
    );
    setPreference.mutate({
      id,
      creatorId: creator.id,
      data: { is_favorite: creator.is_favorite ?? false, is_hidden: newVal },
    });
  }

  const sendOffer = useSendOffer({
    mutation: {
      onSuccess: (data, variables) => {
        logCreate('hiring_offers', (data as any)?.id || variables.data.creator_id, variables.data as Record<string, any>, { campaign_id: id });
        toast({ title: "Offer sent!" });
        setSendingIds((prev) => {
          const next = new Set(prev);
          next.delete(variables.data.creator_id);
          return next;
        });
        setPreviewCreator(null);
        setDraft(null);
        queryClient.invalidateQueries({ queryKey: [`/api/v1/hiring/campaigns/${id}`] });
        queryClient.invalidateQueries({ queryKey: getFindCreatorsQueryKey(id) });
      },
      onError: (error, variables) => {
        errToast("Failed to send offer", error);
        const msg = (error as any)?.data?.error ?? (error as any)?.message ?? "An error occurred";
        logCreate('hiring_offers', variables.data.creator_id, variables.data as Record<string, any>, { campaign_id: id, status: 'failed', error: msg });
        setSendingIds((prev) => {
          const next = new Set(prev);
          next.delete(variables.data.creator_id);
          return next;
        });
      },
    },
  });

  const sendBulkOffers = useSendBulkOffers({
    mutation: {
      onSuccess: (result, variables) => {
        const sentCount = result.sent?.length ?? 0;
        const failedCount = result.failed?.length ?? 0;
        const skippedCount = result.skipped?.length ?? 0;
        const emailFailedCount = result.emailFailed?.length ?? 0;
        const selectedCount = variables.data.creator_ids?.length ?? 0;
        const hasErrors = failedCount > 0 || emailFailedCount > 0;

        const parts = [`${sentCount} offer${sentCount !== 1 ? "s" : ""} sent`];
        if (skippedCount > 0) parts.push(`${skippedCount} skipped`);
        if (failedCount > 0) parts.push(`${failedCount} failed`);
        if (emailFailedCount > 0) parts.push(`${emailFailedCount} email${emailFailedCount !== 1 ? "s" : ""} not delivered`);

        toast({ title: parts.join(", "), variant: hasErrors ? "destructive" : "default" });

        logCreate('hiring_offers_bulk', id, {
          campaign_id: id,
          selected_count: selectedCount,
          sent_count: sentCount,
          failed_count: failedCount,
          skipped_count: skippedCount,
          email_failed_count: emailFailedCount,
          failed_creator_ids: (result.failed ?? []).map((f: any) => f.creator_id ?? f),
          email_failed_creator_ids: (result.emailFailed ?? []).map((f: any) => f.creator_id ?? f),
        }, { campaign_id: id });

        if (emailFailedCount > 0) {
          result.emailFailed.forEach((item) => {
            errToast("Email not delivered — resend manually", item);
          });
        }

        setBulkSending(false);
        setBulkDialogOpen(false);
        setBulkDraft(null);
        setSelectedIds(new Set());
        queryClient.invalidateQueries({ queryKey: [`/api/v1/hiring/campaigns/${id}`] });
        queryClient.invalidateQueries({ queryKey: getFindCreatorsQueryKey(id) });
      },
      onError: (error) => {
        const msg = (error as { error?: string }).error ?? "An error occurred";
        errToast("Bulk send failed", msg);
        logCreate('hiring_offers_bulk', id, { campaign_id: id }, { campaign_id: id, status: 'failed', error: msg });
        setBulkSending(false);
        setBulkDialogOpen(false);
        setBulkDraft(null);
      },
    },
  });

  function buildDefaultDraft(creator: Creator): EmailDraft {
    if (!campaign) {
      return { subject: "", preheader: "", greeting: "", headline: "", ctaText: "", cashPrizesDesc: "", commissionDesc: "" };
    }
    const c = campaign as any;
    return {
      subject: c.email_subject ?? `\uD83D\uDEA8 New 1-on-1 hiring job from ${campaign.brand_name}`,
      preheader: c.email_preheader ?? `${campaign.brand_name} wants to work with you \u2014 $${campaign.offer_amount * campaign.num_videos_per_creator} opportunity`,
      greeting: c.email_greeting ?? `Hey {name},`,
      headline: c.email_headline ?? `You've been hand-selected for a paid TikTok Shop content opportunity with ${campaign.brand_name}.`,
      ctaText: c.email_cta_text ?? "VIEW FULL DETAILS \u2192",
      cashPrizesDesc: c.email_prizes_desc ?? "Compete for bonus prizes based on sales",
      commissionDesc: c.email_commission_desc ?? "on every sale your content drives",
    };
  }

  function handleSendFromPreview() {
    if (!previewCreator || !draft) return;
    const creatorId = previewCreator.id;
    setSendingIds((prev) => new Set(prev).add(creatorId));
    sendOffer.mutate({
      id,
      data: {
        creator_id: creatorId,
        email_subject: draft.subject || undefined,
        email_greeting: draft.greeting || undefined,
        email_headline: draft.headline || undefined,
        email_cta_text: draft.ctaText || undefined,
        email_preheader: draft.preheader || undefined,
        email_prizes_desc: draft.cashPrizesDesc || undefined,
        email_commission_desc: draft.commissionDesc || undefined,
      },
    });
  }

  function handleSaveAsDefault(sourceDraft?: EmailDraft) {
    const d = sourceDraft ?? draft;
    if (!d || !campaign) return;
    setSavingTemplate(true);
    updateCampaign.mutate({
      id,
      data: {
        brand_name: campaign.brand_name,
        offer_amount: campaign.offer_amount,
        num_videos_per_creator: campaign.num_videos_per_creator,
        email_subject: d.subject || null,
        email_greeting: d.greeting || null,
        email_headline: d.headline || null,
        email_cta_text: d.ctaText || null,
        email_preheader: d.preheader || null,
        email_prizes_desc: d.cashPrizesDesc || null,
        email_commission_desc: d.commissionDesc || null,
      },
    });
  }

  function openPreview(creator: Creator) {
    setPreviewCreator(creator);
    setDraft(buildDefaultDraft(creator));
  }

  function updateDraft(field: keyof EmailDraft, value: string) {
    setDraft((prev) => prev ? { ...prev, [field]: value } : prev);
  }

  function updateBulkDraft(field: keyof EmailDraft, value: string) {
    setBulkDraft((prev) => prev ? { ...prev, [field]: value } : prev);
  }

  function handleSearch() {
    setSearch(searchInput);
    setMinGmv(minGmvInput ? Number(minGmvInput) : undefined);
    setMaxGmv(maxGmvInput ? Number(maxGmvInput) : undefined);
    setMinGmvPerVideo(minGmvPerVideoInput ? Number(minGmvPerVideoInput) : undefined);
    setMaxGmvPerVideo(maxGmvPerVideoInput ? Number(maxGmvPerVideoInput) : undefined);
    setMinRoi(minRoiInput ? Number(minRoiInput) : undefined);
    setMaxRoi(maxRoiInput ? Number(maxRoiInput) : undefined);
    setMinRate(minRateInput ? Number(minRateInput) : undefined);
    setMaxRate(maxRateInput ? Number(maxRateInput) : undefined);
    setPage(1);
  }

  const result = data as CreatorSearchResult | undefined;
  const creators: Creator[] = result?.creators ?? [];
  const total = result?.total ?? 0;
  const perPage = result?.per_page ?? 25;
  const categories: string[] = result?.categories ?? [];
  const totalPages = Math.ceil(total / perPage);

  const favoriteCount = creators.filter((c) => (c as any).is_favorite).length;

  const selectableCreators = creators.filter((c) => !c.already_sent);
  const allSelected = selectableCreators.length > 0 && selectableCreators.every((c) => selectedIds.has(c.id));
  const someSelected = !allSelected && selectableCreators.some((c) => selectedIds.has(c.id));

  function toggleSelect(creatorId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(creatorId)) next.delete(creatorId);
      else next.add(creatorId);
      return next;
    });
  }

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableCreators.map((c) => c.id)));
    }
  }

  function openBulkDialog() {
    if (!campaign) return;
    const c = campaign as any;
    setBulkDraft({
      subject: c.email_subject ?? `\uD83D\uDEA8 New 1-on-1 hiring job from ${campaign.brand_name}`,
      preheader: c.email_preheader ?? `${campaign.brand_name} wants to work with you \u2014 $${campaign.offer_amount * campaign.num_videos_per_creator} opportunity`,
      greeting: c.email_greeting ?? `Hey {name},`,
      headline: c.email_headline ?? `You've been hand-selected for a paid TikTok Shop content opportunity with ${campaign.brand_name}.`,
      ctaText: c.email_cta_text ?? "VIEW FULL DETAILS \u2192",
      cashPrizesDesc: c.email_prizes_desc ?? "Compete for bonus prizes based on sales",
      commissionDesc: c.email_commission_desc ?? "on every sale your content drives",
    });
    setBulkDialogOpen(true);
  }

  function handleBulkSend() {
    if (!bulkDraft) return;
    setBulkSending(true);
    sendBulkOffers.mutate({
      id,
      data: {
        creator_ids: [...selectedIds],
        email_subject: bulkDraft.subject || undefined,
        email_greeting: bulkDraft.greeting || undefined,
        email_headline: bulkDraft.headline || undefined,
        email_cta_text: bulkDraft.ctaText || undefined,
        email_preheader: bulkDraft.preheader || undefined,
        email_prizes_desc: bulkDraft.cashPrizesDesc || undefined,
        email_commission_desc: bulkDraft.commissionDesc || undefined,
      },
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild className="text-muted-foreground">
          <Link to={`/hiring/campaigns/${id}`}>
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Campaign
          </Link>
        </Button>
      </div>

      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Find Creators</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Browse your creator roster and send offers.
        </p>
      </div>

      <div className="bg-card border rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <SlidersHorizontal className="h-4 w-4" />
          Filters
        </div>

        {/* Row 1: Search + Category + Sort */}
        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="flex gap-2 flex-1 min-w-[200px]">
            <Input
              placeholder="Search by name or @handle..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              data-testid="input-search-creators"
            />
            <Button onClick={handleSearch} variant="outline" size="icon" className="shrink-0">
              <Search className="h-4 w-4" />
            </Button>
          </div>

          <Select
            value={category ?? "all"}
            onValueChange={(v) => {
              setCategory(v === "all" ? undefined : v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-44" data-testid="select-category">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={gender ?? "all"}
            onValueChange={(v) => {
              setGender(v === "all" ? undefined : v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-36" data-testid="select-gender">
              <SelectValue placeholder="All Genders" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Genders</SelectItem>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={sortBy}
            onValueChange={(v) => {
              setSortBy(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-48" data-testid="select-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={FindCreatorsSortBy.gmv_desc}>Highest 28-Day GMV</SelectItem>
              <SelectItem value={FindCreatorsSortBy.gmv_per_video_desc}>Highest GMV/Video</SelectItem>
              <SelectItem value={FindCreatorsSortBy.roi_desc}>Highest ROI</SelectItem>
              <SelectItem value={FindCreatorsSortBy.name_asc}>Name A–Z</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Row 2: Numeric range filters */}
        <div className="flex flex-wrap gap-4 items-start">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">28-Day GMV ($)</Label>
            <div className="flex items-center gap-1.5">
              <Input type="number" placeholder="Min" className="w-20 h-8 text-sm" value={minGmvInput}
                onChange={(e) => setMinGmvInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} data-testid="input-min-gmv" />
              <span className="text-muted-foreground text-xs">–</span>
              <Input type="number" placeholder="Max" className="w-20 h-8 text-sm" value={maxGmvInput}
                onChange={(e) => setMaxGmvInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} data-testid="input-max-gmv" />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">GMV/Video ($)</Label>
            <div className="flex items-center gap-1.5">
              <Input type="number" placeholder="Min" className="w-20 h-8 text-sm" value={minGmvPerVideoInput}
                onChange={(e) => setMinGmvPerVideoInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} />
              <span className="text-muted-foreground text-xs">–</span>
              <Input type="number" placeholder="Max" className="w-20 h-8 text-sm" value={maxGmvPerVideoInput}
                onChange={(e) => setMaxGmvPerVideoInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">ROI (x)</Label>
            <div className="flex items-center gap-1.5">
              <Input type="number" placeholder="Min" className="w-20 h-8 text-sm" value={minRoiInput}
                onChange={(e) => setMinRoiInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} />
              <span className="text-muted-foreground text-xs">–</span>
              <Input type="number" placeholder="Max" className="w-20 h-8 text-sm" value={maxRoiInput}
                onChange={(e) => setMaxRoiInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} />
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Rate ($/vid)</Label>
            <div className="flex items-center gap-1.5">
              <Input type="number" placeholder="Min" className="w-20 h-8 text-sm" value={minRateInput}
                onChange={(e) => setMinRateInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} />
              <span className="text-muted-foreground text-xs">–</span>
              <Input type="number" placeholder="Max" className="w-20 h-8 text-sm" value={maxRateInput}
                onChange={(e) => setMaxRateInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} />
            </div>
          </div>

          <Button onClick={handleSearch} size="sm" variant="secondary" className="mt-auto">
            Apply Filters
          </Button>
        </div>

        {/* Row 3: Toggle filters */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setShowFavoritesOnly(!showFavoritesOnly); setPage(1); }}
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
              showFavoritesOnly
                ? "bg-rose-50 border-rose-300 text-rose-700"
                : "bg-muted/40 border-border text-muted-foreground hover:bg-muted"
            }`}
          >
            <Heart className={`h-3.5 w-3.5 ${showFavoritesOnly ? "fill-rose-500 text-rose-500" : ""}`} />
            Favorites Only
          </button>
          <button
            onClick={() => { setShowHidden(!showHidden); setPage(1); }}
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
              showHidden
                ? "bg-slate-100 border-slate-400 text-slate-700"
                : "bg-muted/40 border-border text-muted-foreground hover:bg-muted"
            }`}
          >
            {showHidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            {showHidden ? "Showing Hidden" : "Hidden Excluded"}
          </button>
        </div>
      </div>

      {!isLoading && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {total} creator{total !== 1 ? "s" : ""} found
            {showFavoritesOnly && <span className="ml-1 text-rose-600 font-medium">· Favorites only</span>}
            {showHidden && <span className="ml-1 text-slate-500 font-medium">· Includes hidden</span>}
          </p>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-lg px-4 py-2">
              <span className="text-sm font-medium">{selectedIds.size} selected</span>
              <Button size="sm" onClick={openBulkDialog} disabled={!campaign}>
                <Send className="h-3.5 w-3.5 mr-1.5" />
                Send to {selectedIds.size}
              </Button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}

      <div className="rounded-md border bg-card overflow-hidden overflow-x-auto">
        {/* Exclusive Campaigns tables keep the old scroll-sideways layout. */}
        <Table className={WIDE_TABLE_SCROLL}>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  data-state={someSelected ? "indeterminate" : allSelected ? "checked" : "unchecked"}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all"
                  disabled={selectableCreators.length === 0}
                />
              </TableHead>
              <TableHead>Name</TableHead>
              <TableHead>TikTok</TableHead>
              <TableHead className="text-right">28-Day GMV</TableHead>
              <TableHead className="text-right">GMV/Video</TableHead>
              <TableHead className="text-right">ROI</TableHead>
              <TableHead>Categories</TableHead>
              <TableHead>Level</TableHead>
              <TableHead>Rate</TableHead>
              <TableHead>Gender</TableHead>
              <TableHead className="w-[140px] text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 12 }).map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : creators?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} className="h-32 text-center text-muted-foreground">
                  No creators found matching your filters.
                </TableCell>
              </TableRow>
            ) : (
              creators?.map((creator) => {
                const c = creator as any;
                const isFav = c.is_favorite ?? false;
                const isHidden = c.is_hidden ?? false;
                return (
                  <TableRow
                    key={creator.id}
                    className={`${creator.already_sent ? "opacity-60" : ""} ${isHidden ? "opacity-40" : ""}`}
                  >
                    {/* Favorite/Hide icon column */}
                    <TableCell className="pr-0">
                      <div className="flex flex-col gap-1 items-center">
                        <button
                          onClick={() => toggleFavorite(creator)}
                          title={isFav ? "Remove from favorites" : "Add to favorites"}
                          className={`p-0.5 rounded transition-colors ${isFav ? "text-rose-500 hover:text-rose-400" : "text-muted-foreground/30 hover:text-rose-400"}`}
                        >
                          <Heart className={`h-3.5 w-3.5 ${isFav ? "fill-rose-500" : ""}`} />
                        </button>
                        <button
                          onClick={() => toggleHidden(creator)}
                          title={isHidden ? "Show this creator" : "Hide this creator"}
                          className={`p-0.5 rounded transition-colors ${isHidden ? "text-slate-500 hover:text-slate-400" : "text-muted-foreground/30 hover:text-slate-500"}`}
                        >
                          {isHidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </TableCell>
                    <TableCell>
                      {!creator.already_sent && (
                        <Checkbox
                          checked={selectedIds.has(creator.id)}
                          onCheckedChange={() => toggleSelect(creator.id)}
                          aria-label={`Select ${creator.name ?? creator.id}`}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium whitespace-nowrap flex items-center gap-1.5">
                        {creator.name ?? creator.tiktok_username ?? "Unknown"}
                        {isFav && <Star className="h-3 w-3 fill-amber-400 text-amber-400 shrink-0" />}
                      </div>
                      {creator.email && (
                        <div className="text-xs text-muted-foreground">{creator.email}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      {creator.tiktok_username ? (
                        creator.tiktok_url ? (
                          <a
                            href={creator.tiktok_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-blue-600 hover:underline whitespace-nowrap"
                          >
                            @{creator.tiktok_username}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span className="text-sm whitespace-nowrap">@{creator.tiktok_username}</span>
                        )
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium whitespace-nowrap">
                      {fmtGmv(creator.creator_gmv)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground whitespace-nowrap">
                      {fmtGmv(creator.gmv_per_video)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground whitespace-nowrap">
                      {fmtRoi(creator.roi)}
                    </TableCell>
                    <TableCell className="max-w-[160px]">
                      <div className="flex flex-wrap gap-1">
                        {(creator.categories ?? []).slice(0, 3).map((cat) => (
                          <Badge key={cat} variant="outline" className="text-xs">
                            {cat}
                          </Badge>
                        ))}
                        {(creator.categories?.length ?? 0) > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{(creator.categories?.length ?? 0) - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {creator.creator_level ? (
                        <Badge variant="outline" className="text-xs whitespace-nowrap">
                          {creator.creator_level}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {fmtRate(creator.video_prices)}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap capitalize text-muted-foreground">
                      {c?.gender ?? "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {creator.already_sent ? (
                        <span className="flex items-center justify-end gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                          Sent
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => openPreview(creator)}
                          disabled={sendingIds.has(creator.id)}
                          data-testid={`btn-send-offer-${creator.id}`}
                        >
                          {sendingIds.has(creator.id) ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <Eye className="h-3.5 w-3.5 mr-1.5" />
                          )}
                          Preview & Send
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total} creators
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
              <PrevIcon className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
              <NextIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={bulkDialogOpen} onOpenChange={(open) => { if (!open && !bulkSending) { setBulkDialogOpen(false); setBulkDraft(null); } }}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <div className="px-6 py-4 border-b shrink-0">
            <DialogTitle>Bulk Send — {selectedIds.size} Creator{selectedIds.size !== 1 ? "s" : ""}</DialogTitle>
            <DialogDescription>
              Edit any text on the left. The right shows exactly what will be sent. The same email goes to all selected creators.
            </DialogDescription>
          </div>

          {bulkDraft && campaign && (() => {
            const total = campaign.offer_amount * campaign.num_videos_per_creator;
            const hasRewards = !!(campaign as any).has_rewards;
            const prizePool: { place: number; amount: number }[] = (campaign as any).prize_pool || [];
            const firstPlace = prizePool.length > 0 ? prizePool[0].amount : 0;
            const totalPrize = prizePool.reduce((s, p) => s + p.amount, 0);
            const earnUpTo = total + firstPlace;

            const infoBox = (editable: boolean) => (
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-200 text-[11px]">
                <div className="px-2.5 py-2 bg-slate-50 rounded-t-lg">
                  <p className="font-extrabold text-slate-900 text-xs">
                    {hasRewards && firstPlace > 0
                      ? `Earn up to $${earnUpTo.toLocaleString()}+ plus commission`
                      : `Earn $${total.toLocaleString()} guaranteed plus commission`}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    {hasRewards && firstPlace > 0
                      ? `$${total.toLocaleString()} guaranteed · up to $${firstPlace.toLocaleString()} in prizes · uncapped commission`
                      : `$${campaign.offer_amount}/video × ${campaign.num_videos_per_creator} videos + uncapped TikTok Shop commission`}
                  </p>
                </div>
                <div className="px-2.5 py-2">
                  <p className="font-bold text-slate-900">💰 GUARANTEED PAY</p>
                  <p className="text-slate-600">${campaign.offer_amount}/video × {campaign.num_videos_per_creator} videos = ${total.toLocaleString()}</p>
                </div>
                {hasRewards && (
                  <div className="px-2.5 py-2">
                    <p className="font-bold text-slate-900">🏆 CASH PRIZES ({totalPrize > 0 ? `$${totalPrize.toLocaleString()} total` : ""})</p>
                    {editable
                      ? <Textarea value={bulkDraft.cashPrizesDesc} onChange={(e) => updateBulkDraft("cashPrizesDesc", e.target.value)} className="mt-1 text-[11px] h-12 resize-none" />
                      : <p className="text-slate-600">{bulkDraft.cashPrizesDesc}</p>
                    }
                  </div>
                )}
                <div className="px-2.5 py-2">
                  <p className="font-bold text-slate-900">📈 TIKTOK SHOP COMMISSION</p>
                  {editable
                    ? <Textarea value={bulkDraft.commissionDesc} onChange={(e) => updateBulkDraft("commissionDesc", e.target.value)} className="mt-1 text-[11px] h-10 resize-none" />
                    : <p className="text-slate-600">{bulkDraft.commissionDesc}</p>
                  }
                </div>
              </div>
            );

            const emailPreview = (editable: boolean) => (
              <div className="font-sans text-[13px] leading-relaxed text-slate-800 space-y-4">
                <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
                  <img src="/bmc-logo.png" alt="BMC" className="h-6" />
                  <div className="text-[10px] text-slate-400 space-y-0.5">
                    <p><span className="font-medium">Subject:</span> {editable
                      ? <input className="ml-1 border-0 border-b border-slate-300 focus:outline-none text-[10px] w-48" value={bulkDraft.subject} onChange={(e) => updateBulkDraft("subject", e.target.value)} />
                      : bulkDraft.subject
                    }</p>
                    <p><span className="font-medium">Preview:</span> {editable
                      ? <input className="ml-1 border-0 border-b border-slate-300 focus:outline-none text-[10px] w-48" value={bulkDraft.preheader} onChange={(e) => updateBulkDraft("preheader", e.target.value)} />
                      : bulkDraft.preheader
                    }</p>
                  </div>
                </div>
                <p>
                  {editable
                    ? <input className="border-0 border-b border-slate-300 focus:outline-none w-full" value={bulkDraft.greeting} onChange={(e) => updateBulkDraft("greeting", e.target.value)} />
                    : bulkDraft.greeting
                  }
                </p>
                <p className="font-semibold text-slate-900">
                  {editable
                    ? <Textarea value={bulkDraft.headline} onChange={(e) => updateBulkDraft("headline", e.target.value)} className="text-[13px] font-semibold resize-none h-16" />
                    : bulkDraft.headline
                  }
                </p>
                {infoBox(editable)}
                <div className="text-center">
                  <div className="inline-block bg-[#FB3AAE] text-white font-bold text-xs px-6 py-2.5 rounded-lg">
                    {editable
                      ? <input className="bg-transparent text-white text-center border-0 outline-none w-40" value={bulkDraft.ctaText} onChange={(e) => updateBulkDraft("ctaText", e.target.value)} />
                      : bulkDraft.ctaText
                    }
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 text-center">
                  Each link is unique to the recipient. Offer expires in 7 days.
                </p>
              </div>
            );

            return (
              <div className="flex flex-1 overflow-hidden divide-x">
                <div className="w-1/2 flex flex-col overflow-hidden">
                  <div className="px-4 py-2 bg-muted/30 border-b text-xs font-medium text-muted-foreground">Edit</div>
                  <div className="flex-1 overflow-y-auto p-4">{emailPreview(true)}</div>
                  <div className="border-t p-3 flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => handleSaveAsDefault(bulkDraft)} disabled={savingTemplate}>
                      <Save className="h-3.5 w-3.5 mr-1.5" />
                      {savingTemplate ? "Saving…" : "Save as Default"}
                    </Button>
                  </div>
                </div>
                <div className="w-1/2 flex flex-col overflow-hidden">
                  <div className="px-4 py-2 bg-muted/30 border-b text-xs font-medium text-muted-foreground">Preview</div>
                  <div className="flex-1 overflow-y-auto p-4">{emailPreview(false)}</div>
                  <div className="border-t p-3 flex justify-end shrink-0">
                    <Button onClick={handleBulkSend} disabled={bulkSending}>
                      {bulkSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      Send to {selectedIds.size} creator{selectedIds.size !== 1 ? "s" : ""}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewCreator} onOpenChange={(open) => { if (!open) { setPreviewCreator(null); setDraft(null); } }}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <div className="px-6 py-4 border-b shrink-0">
            <DialogTitle>Compose & Preview</DialogTitle>
            <DialogDescription>
              Edit any text on the left. The right shows exactly what will be sent.
            </DialogDescription>
          </div>

          {draft && campaign && (() => {
            const total = campaign.offer_amount * campaign.num_videos_per_creator;
            const hasRewards = !!(campaign as any).has_rewards;
            const prizePool: { place: number; amount: number }[] = (campaign as any).prize_pool || [];
            const firstPlace = prizePool.length > 0 ? prizePool[0].amount : 0;
            const totalPrize = prizePool.reduce((s, p) => s + p.amount, 0);
            const earnUpTo = total + firstPlace;
            const creatorDisplayName = previewCreator?.name ?? previewCreator?.tiktok_username ?? "Creator";

            const infoBox = (editable: boolean) => (
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-200 text-[11px]">
                <div className="px-2.5 py-2 bg-slate-50 rounded-t-lg">
                  <p className="font-extrabold text-slate-900 text-xs">
                    {hasRewards && firstPlace > 0
                      ? `Earn up to $${earnUpTo.toLocaleString()}+ plus commission`
                      : `Earn $${total.toLocaleString()} guaranteed plus commission`}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    {hasRewards && firstPlace > 0
                      ? `$${total.toLocaleString()} guaranteed · up to $${firstPlace.toLocaleString()} in prizes · uncapped commission`
                      : `$${campaign.offer_amount}/video × ${campaign.num_videos_per_creator} videos + uncapped TikTok Shop commission`}
                  </p>
                </div>
                <div className="px-2.5 py-2">
                  <p className="font-bold text-slate-900">💰 GUARANTEED PAY</p>
                  <p className="text-slate-600">${campaign.offer_amount}/video × {campaign.num_videos_per_creator} videos = ${total.toLocaleString()}</p>
                </div>
                {hasRewards && (
                  <div className="px-2.5 py-2">
                    <p className="font-bold text-slate-900">🏆 CASH PRIZES ({totalPrize > 0 ? `$${totalPrize.toLocaleString()} total` : ""})</p>
                    {editable
                      ? <Textarea value={draft.cashPrizesDesc} onChange={(e) => updateDraft("cashPrizesDesc", e.target.value)} className="mt-1 text-[11px] h-12 resize-none" />
                      : <p className="text-slate-600">{draft.cashPrizesDesc}</p>
                    }
                  </div>
                )}
                <div className="px-2.5 py-2">
                  <p className="font-bold text-slate-900">📈 TIKTOK SHOP COMMISSION</p>
                  {editable
                    ? <Textarea value={draft.commissionDesc} onChange={(e) => updateDraft("commissionDesc", e.target.value)} className="mt-1 text-[11px] h-10 resize-none" />
                    : <p className="text-slate-600">{draft.commissionDesc}</p>
                  }
                </div>
              </div>
            );

            const emailPreview = (editable: boolean) => (
              <div className="font-sans text-[13px] leading-relaxed text-slate-800 space-y-4">
                <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
                  <img src="/bmc-logo.png" alt="BMC" className="h-6" />
                  <div className="text-[10px] text-slate-400 space-y-0.5">
                    <p><span className="font-medium">Subject:</span> {editable
                      ? <input className="ml-1 border-0 border-b border-slate-300 focus:outline-none text-[10px] w-48" value={draft.subject} onChange={(e) => updateDraft("subject", e.target.value)} />
                      : draft.subject
                    }</p>
                    <p><span className="font-medium">Preview:</span> {editable
                      ? <input className="ml-1 border-0 border-b border-slate-300 focus:outline-none text-[10px] w-48" value={draft.preheader} onChange={(e) => updateDraft("preheader", e.target.value)} />
                      : draft.preheader
                    }</p>
                  </div>
                </div>
                <p>
                  {editable
                    ? <input className="border-0 border-b border-slate-300 focus:outline-none w-full" value={draft.greeting} onChange={(e) => updateDraft("greeting", e.target.value)} />
                    : draft.greeting.replace("{name}", creatorDisplayName)
                  }
                </p>
                <p className="font-semibold text-slate-900">
                  {editable
                    ? <Textarea value={draft.headline} onChange={(e) => updateDraft("headline", e.target.value)} className="text-[13px] font-semibold resize-none h-16" />
                    : draft.headline
                  }
                </p>
                {infoBox(editable)}
                <div className="text-center">
                  <div className="inline-block bg-[#FB3AAE] text-white font-bold text-xs px-6 py-2.5 rounded-lg">
                    {editable
                      ? <input className="bg-transparent text-white text-center border-0 outline-none w-40" value={draft.ctaText} onChange={(e) => updateDraft("ctaText", e.target.value)} />
                      : draft.ctaText
                    }
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 text-center">
                  This link is unique to {creatorDisplayName}. Offer expires in 7 days.
                </p>
              </div>
            );

            return (
              <div className="flex flex-1 overflow-hidden divide-x">
                <div className="w-1/2 flex flex-col overflow-hidden">
                  <div className="px-4 py-2 bg-muted/30 border-b text-xs font-medium text-muted-foreground">Edit</div>
                  <div className="flex-1 overflow-y-auto p-4">{emailPreview(true)}</div>
                  <div className="border-t p-3 flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={handleSaveAsDefault} disabled={savingTemplate}>
                      <Save className="h-3.5 w-3.5 mr-1.5" />
                      {savingTemplate ? "Saving…" : "Save as Default"}
                    </Button>
                  </div>
                </div>
                <div className="w-1/2 flex flex-col overflow-hidden">
                  <div className="px-4 py-2 bg-muted/30 border-b text-xs font-medium text-muted-foreground">Preview</div>
                  <div className="flex-1 overflow-y-auto p-4">{emailPreview(false)}</div>
                  <div className="border-t p-3 flex justify-end shrink-0">
                    <Button onClick={handleSendFromPreview} disabled={sendingIds.has(previewCreator?.id ?? "")}>
                      {sendingIds.has(previewCreator?.id ?? "") ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Send to {previewCreator?.name ?? previewCreator?.tiktok_username ?? "Creator"}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
